import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { tenantConfig } from '../config/tenant'
import Nav from '../components/Nav'
import Seo from '../components/Seo'
import Footer from '../components/Footer'
import LeadCaptureModal from '../components/LeadCaptureModal'
import suburbsData from '../data/suburbs.json'

/**
 * SuburbPage — Phase 6 programmatic suburb SEO landing (Chelston commitment C4).
 *
 * One React component serves all 30 target suburbs at /suburbs/:state/:slug — the
 * exact URL pattern already emitted by public/sitemap.xml. At build time the Vite
 * SSG plugin pre-renders one static HTML file per suburb (reading data/suburbs.json,
 * which is the single source of truth and is NEVER mutated here), so every suburb
 * ships crawlable HTML with its own Schema.org JSON-LD.
 *
 * Kept deliberately minimal per §4.1 (minimalism): a suburb hero, a
 * "thinking of buying/refinancing in <suburb>?" value section, a talk-to-a-broker
 * CTA, and the Schema.org block. No fabricated local stats or invented staff.
 */

interface Suburb {
  name: string
  state: string
  slug: string
  postcode: string
}

const SUBURBS = (suburbsData as { suburbs: Suburb[] }).suburbs

export default function SuburbPage() {
  const { state, slug } = useParams<{ state: string; slug: string }>()
  const [leadOpen, setLeadOpen] = useState(false)

  const suburb = SUBURBS.find(
    (s) => s.slug === slug && (state == null || s.state === state),
  )

  if (!suburb) {
    return (
      <>
        <Nav />
        <main id="main" className="pt-36 pb-20 max-w-3xl mx-auto px-6">
          <h1 className="font-heading text-3xl text-ink">Suburb not found</h1>
          <p className="text-gs-text-soft mt-2">
            We don&apos;t have a dedicated page for that suburb yet, but we lend
            Australia-wide. Get in touch and we&apos;ll help wherever you&apos;re buying.
          </p>
          <a href="/" className="mt-4 inline-block text-sm underline">
            Go home
          </a>
        </main>
        <Footer />
      </>
    )
  }

  const stateUpper = suburb.state.toUpperCase()
  const location = `${suburb.name}, ${stateUpper} ${suburb.postcode}`
  const path = `/suburbs/${suburb.state}/${suburb.slug}`
  const canonical = `${tenantConfig.siteUrl}${path}`

  // Schema.org markup — a RealEstateAgent (the broker) whose service area is the
  // suburb Place. Serialised via dangerouslySetInnerHTML so crawlers get raw JSON.
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'RealEstateAgent',
    name: `${tenantConfig.name} · Mortgage Brokers in ${suburb.name}`,
    description: `Local mortgage broking and home-loan advice for buyers and homeowners in ${location}.`,
    url: canonical,
    areaServed: {
      '@type': 'Place',
      name: location,
      address: {
        '@type': 'PostalAddress',
        addressLocality: suburb.name,
        addressRegion: stateUpper,
        postalCode: suburb.postcode,
        addressCountry: 'AU',
      },
    },
    priceRange: 'Free initial consultation',
  }

  const VALUE_POINTS = [
    {
      title: 'Local market, real numbers',
      body: `We know what lenders will actually approve for ${suburb.name} buyers, not a generic online estimate.`,
    },
    {
      title: 'Whole-of-market lender panel',
      body: 'We compare across our full panel of banks and non-bank lenders to find the structure that fits your situation.',
    },
    {
      title: 'Buying or refinancing',
      body: `Whether it's your first ${suburb.name} home, an upgrade, an investment purchase or a refinance, we handle the paperwork end to end.`,
    },
  ]

  return (
    <>
      <Seo
        title={`${suburb.name} Home Loans & Mortgage Brokers`}
        description={`Local mortgage broking and home-loan advice for buyers and homeowners in ${location}. Compare ${tenantConfig.lenderCount}+ lenders with ${tenantConfig.name}.`}
        path={path}
        jsonLd={jsonLd}
      />
      <Nav />
      <main id="main" className="pt-36 pb-20">
        {/* HERO */}
        <section className="max-w-5xl mx-auto px-6 pb-16">
          <div className="inline-flex items-center gap-2 border border-[#E8E8E2] rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wider text-gs-text-soft mb-6">
            <span
              className="w-1.5 h-1.5 rounded-full"
              style={{ background: tenantConfig.accentColor }}
            />
            Local mortgage brokers · {stateUpper} {suburb.postcode}
          </div>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-ink font-semibold leading-tight mb-4">
            Home loans in {suburb.name}, {stateUpper}
          </h1>
          <p className="text-lg text-gs-text-soft max-w-3xl">
            {tenantConfig.name} helps {suburb.name} buyers, upgraders and homeowners
            get the right home loan, comparing across our whole lender panel and doing
            the legwork for you. Local knowledge, no obligation, no cost to you.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <button
              onClick={() => setLeadOpen(true)}
              className="px-6 py-3.5 rounded-lg font-semibold text-sm"
              style={{ background: tenantConfig.accentColor, color: tenantConfig.primaryColor }}
            >
              Talk to a broker
            </button>
            <Link
              to="/contact"
              className="px-6 py-3.5 rounded-lg font-semibold text-sm border border-[#D8D8D0] hover:bg-white transition-colors"
            >
              Book a call
            </Link>
          </div>
        </section>

        {/* VALUE SECTION */}
        <section className="max-w-6xl mx-auto px-6 pb-16">
          <div className="mb-8">
            <div className="text-xs font-semibold uppercase tracking-wider text-gs-text-muted mb-2">
              Why {suburb.name}
            </div>
            <h2 className="font-heading text-3xl md:text-4xl text-ink">
              Thinking of buying or refinancing in {suburb.name}?
            </h2>
            <p className="text-gs-text-soft mt-3 max-w-3xl">
              {suburb.name} ({stateUpper} {suburb.postcode}) is one of the areas we work in
              every week. Here&apos;s what you get with a broker who knows the local market.
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {VALUE_POINTS.map((v) => (
              <div
                key={v.title}
                className="p-6 rounded-2xl border border-[#E8E8E2] bg-white card-hover"
              >
                <h3 className="font-heading text-lg text-ink mb-2">{v.title}</h3>
                <p className="text-sm text-gs-text-soft">{v.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA BAND */}
        <section className="max-w-6xl mx-auto px-6">
          <div
            className="rounded-3xl p-10 md:p-14 grid gap-6 md:grid-cols-[1.5fr_1fr] items-center"
            style={{ background: tenantConfig.primaryColor }}
          >
            <div>
              <h2
                className="font-heading text-3xl md:text-4xl mb-3"
                style={{ color: tenantConfig.accentColor }}
              >
                Get a {suburb.name} home-loan game plan
              </h2>
              <p className="text-white/80 text-lg">
                Tell us what you&apos;re looking at and we&apos;ll come back with the
                lenders and numbers that make sense for you. No cost, no pressure.
              </p>
            </div>
            <button
              onClick={() => setLeadOpen(true)}
              className="px-6 py-4 rounded-lg font-semibold text-base w-full md:w-auto justify-self-start md:justify-self-end"
              style={{ background: tenantConfig.accentColor, color: tenantConfig.primaryColor }}
            >
              Talk to a broker
            </button>
          </div>
        </section>

        {/* DISCLAIMER */}
        <section className="max-w-4xl mx-auto px-6 mt-12">
          <p className="text-xs text-gs-text-muted text-center">
            General information only. Not personal financial advice. Lending is subject to
            approval, terms and conditions.
          </p>
        </section>
      </main>
      <Footer />

      <LeadCaptureModal
        open={leadOpen}
        onClose={() => setLeadOpen(false)}
        source="website"
        context={{ state: suburb.state }}
      />
    </>
  )
}
